<template>
  <Modal
    :show="showModal"
    :title="t('Detalhes do Usuário')"
    modal-size="modal-md"
    :save-label="getSaveLabel()"
    :save-disabled-fn="getSaveDisabled"
    :save-fn="getSaveFn()"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="showModal">
      <UserForm
        v-if="currentUser"
        ref="formRef"
        :user="currentUser"
        @save-success="handleSaveSuccess"
        @user-loaded="handleUserLoaded"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import UserForm from './UserForm.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface User {
  username?: string;
  name?: string;
  email?: string;
  sub?: string;
  Usuario?: User;
  [key: string]: unknown;
}

interface UserFormInstance {
  activeTab: number;
  isSubmitting: boolean;
  canSave: boolean;
  initializeForm: () => void;
  handleSubmit: () => Promise<void> | void;
}

interface ModalOpenPayload {
  user?: User;
  id?: string;
}

// Props
const props = defineProps<{
  user?: User | null;
  show?: boolean;
}>();

// Emits
const emit = defineEmits<{
  close: [];
  save: [];
}>();

const { t } = useI18n();

// Data
const formRef = ref<UserFormInstance | null>(null);
const modalShow = ref<boolean>(false);
const modalUser = ref<User | null>(null);
const userData = ref<{ username: string; name: string; email: string }>({
  username: '',
  name: '',
  email: ''
});

// Computed
const showModal = computed<boolean>(() => {
  return modalShow.value || Boolean(props.show);
});

const currentUser = computed<User | undefined>(() => {
  return modalUser.value || props.user || undefined;
});

// Methods
const getSaveLabel = (): string | undefined => {
  // Only show save button on Info tab (index 0)
  if (formRef.value?.activeTab !== 0) {
    return undefined;
  }
  return formRef.value?.isSubmitting ? t('Salvando...') : t('Salvar');
};

const getSaveDisabled = (): boolean => {
  // Disable save if not on Info tab, form is invalid, or submitting
  return formRef.value?.activeTab !== 0 ||
         !formRef.value?.canSave ||
         Boolean(formRef.value?.isSubmitting);
};

const getSaveFn = (): (() => Promise<void>) | undefined => {
  // Only provide save function on Info tab
  return formRef.value?.activeTab === 0 ? handleSave : undefined;
};

const handleUserLoaded = (user: User | null): void => {
  log('UserModal', 'handleUserLoaded', 'User data received', user);

  // Store user data for potential use in modal
  if (user) {
    userData.value.username = user.username || '';
    userData.value.name = user.name || '';
    userData.value.email = user.email || '';
  }
};

const handleClose = (): void => {
  log('UserModal', 'handleClose', 'Closing modal');

  // Reset global modal state
  modalShow.value = false;
  modalUser.value = null;

  emit('close');
};

const handleSave = async (): Promise<void> => {
  log('UserModal', 'handleSave', 'Triggering form submit');
  if (formRef.value) {
    await formRef.value.handleSubmit();
  }
};

const handleSaveSuccess = (): void => {
  log('UserModal', 'handleSaveSuccess', 'User saved successfully');
  emit('save');

  // Close modal after successful save
  handleClose();
};

// Legacy EventBus handler
const handleEditUserEvent = (user?: User): void => {
  log('UserModal', 'handleEditUserEvent', 'Received edit-user event', user);
  modalUser.value = user || null;
  modalShow.value = true;
};

// Standard modal open handler (for modal.user.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('UserModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data?.user) {
    modalUser.value = data.user;
  } else if (data?.id) {
    modalUser.value = { username: data.id };
  }
  modalShow.value = true;
};

// Methods for initializing form
const initializeFormIfReady = async (): Promise<void> => {
  log('UserModal', 'initializeFormIfReady', 'Checking if form is ready');

  // Wait a bit for the DOM to settle
  await nextTick();
  await nextTick(); // Sometimes need double nextTick for nested components

  if (formRef.value) {
    log('UserModal', 'initializeFormIfReady', 'Form ref is available, initializing');
    formRef.value.initializeForm();
  } else {
    log('UserModal', 'initializeFormIfReady', 'Form ref not yet available, will try again');
    // Try again after a short delay
    setTimeout(async () => {
      await nextTick();
      if (formRef.value) {
        log('UserModal', 'initializeFormIfReady', 'Form ref available on retry, initializing');
        formRef.value.initializeForm();
      } else {
        log('UserModal', 'initializeFormIfReady', 'ERROR: Form ref still not available after retry');
      }
    }, 50);
  }
};

// Watchers
watch(() => props.show, async (newValue) => {
  log('UserModal', 'watch:show', 'Show changed to', newValue);
  if (newValue) {
    // Reset to Info tab when modal opens
    await nextTick();
    if (formRef.value) {
      formRef.value.activeTab = 0;
    }
    // Initialize form when modal becomes visible
    await initializeFormIfReady();
  }
});

watch(() => props.user, async (newValue) => {
  log('UserModal', 'watch:user', 'User changed', newValue);
  if (newValue) {
    // Reset to Info tab when user changes
    await nextTick();
    if (formRef.value) {
      formRef.value.activeTab = 0;
    }
  }
});

watch(modalShow, async (newValue) => {
  log('UserModal', 'watch:modalShow', 'Modal show changed to', newValue);
  if (newValue) {
    // Initialize form when global modal becomes visible
    await initializeFormIfReady();
  }
});

// Lifecycle
onMounted(() => {
  log('UserModal', 'onMounted', 'Modal mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.user.open', handleModalOpen);
  eventBus.on('modal.user.close', handleClose);

  // Legacy events (for backward compatibility)
  eventBus.on('user.edit', handleEditUserEvent);

  // If modal is already visible, initialize form
  if (showModal.value) {
    log('UserModal', 'onMounted', 'Modal is visible, initializing form');
    initializeFormIfReady();
  }
});

onBeforeUnmount(() => {
  log('UserModal', 'beforeUnmount', 'Component unmounting');

  // Remove standard modal events
  eventBus.off('modal.user.open', handleModalOpen);
  eventBus.off('modal.user.close', handleClose);

  // Remove legacy events
  eventBus.off('user.edit', handleEditUserEvent);
});
</script>

<style scoped>
/* No additional styles needed - all styling is in UserForm.vue */
</style>
